import { ReactNode, useContext, useState, useEffect } from 'react';
import { QueryClientContext } from '@tanstack/react-query';
import { Redirect, useLocation } from 'wouter';
import { useAuth } from '@/contexts/auth-context';
import { organizationService } from '@/services/api/organization.service';
import { homePathForRole } from '@/contexts/role-home';
import { User } from '@/types/auth';
import { Spinner } from '@/components/ui/spinner';
import { stashDeepLinkForPath } from '@/features/activationEmails/deepLink';
import { useGateHold } from '@/features/authGate/useGateHold';
import { dropHandedOffOnboarding, handedOffOnboarding } from '@/features/authGate/onboardingHandoff';

type Role = User['role'];

/**
 * What each `requiredRole` admits. Roles are sets, not levels: an estimator is neither above crew nor below staff.
 * Crew routes (My day) are open to everyone who works jobs or visits.
 */
const ROLE_SETS: Record<Role, Role[]> = {
  admin: ['admin'],
  staff: ['admin', 'staff'],
  crew: ['admin', 'staff', 'crew', 'estimator'],
  estimator: ['admin', 'staff', 'estimator'],
  customer: ['admin', 'staff', 'crew', 'estimator', 'customer'],
};

interface ProtectedRouteProps {
  children: ReactNode;
  /** Shorthand for a role set (see ROLE_SETS). */
  requiredRole?: Role;
  /** An explicit role set; wins over requiredRole. */
  allow?: Role[];
  redirectTo?: string;
}

export function ProtectedRoute({
  children,
  requiredRole,
  allow,
  redirectTo = '/login'
}: ProtectedRouteProps) {
  const { user, loading, isAuthenticated, impersonation } = useAuth();
  const [location] = useLocation();
  const [checkingOnboarding, setCheckingOnboarding] = useState(false);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);
  const [onboardingChecked, setOnboardingChecked] = useState(false);
  const queryClient = useContext(QueryClientContext);

  // Reset onboarding check when user is on the onboarding page
  // This ensures we re-check when they navigate away after completing
  useEffect(() => {
    if (location === '/admin/onboarding') {
      // whatever sign-in handed over is out of date once the owner is on the first-run page
      dropHandedOffOnboarding(queryClient);
      setNeedsOnboarding(false);
      setOnboardingChecked(false);
    }
  }, [location, queryClient]);

  // Check onboarding status for admin users on admin routes
  useEffect(() => {
    const checkOnboarding = async () => {
      // Only check for admin users on admin routes (except onboarding page itself).
      // Skip while impersonating — a super-admin should land on the target's
      // dashboard, not be bounced into that tenant's onboarding wizard.
      if (
        user?.role === 'admin' &&
        !impersonation &&
        location.startsWith('/admin') &&
        location !== '/admin/onboarding' &&
        !onboardingChecked
      ) {
        setCheckingOnboarding(true);
        try {
          // sign-in has just read this answer (completed or skipped, which never goes back): reused for up to a minute while nothing was written, otherwise asked for
          const onboarding = handedOffOnboarding(queryClient) ?? (await organizationService.getOnboardingStatus()).onboarding;
          if (!onboarding.walkthrough && !onboarding.completed && !onboarding.skipped) {
            setNeedsOnboarding(true);
          } else {
            setNeedsOnboarding(false);
          }
        } catch (error) {
          console.error('Failed to check onboarding status:', error);
          // Continue without redirect if check fails
        }
        setCheckingOnboarding(false);
        setOnboardingChecked(true);
      }
    };

    if (isAuthenticated && !loading) {
      checkOnboarding();
    }
  }, [user, location, isAuthenticated, loading, onboardingChecked, impersonation, queryClient]);

  // A link from a setup email (exactly one of three owner paths) that is about to be redirected away, to sign-in or to first run: keep which one, so the
  // owner comes back to it (see features/activationEmails/deepLink.ts). In an effect, once per change of state, not on every render.
  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || (needsOnboarding && location !== '/admin/onboarding')) stashDeepLinkForPath(location);
  }, [loading, isAuthenticated, needsOnboarding, location]);

  // The first-run check has not run yet for this admin page: the same render that first shows the page must already be the spinner, not the page for a
  // frame and then the spinner (the effect above only flips `checkingOnboarding` after paint). It also keeps the auth gate up while the check runs.
  const needsOnboardingCheck =
    isAuthenticated && !loading && user?.role === 'admin' && !impersonation && location.startsWith('/admin') && location !== '/admin/onboarding' && !onboardingChecked;
  useGateHold(needsOnboardingCheck || checkingOnboarding);

  if (loading || checkingOnboarding || needsOnboardingCheck) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Redirect to={redirectTo} />;
  }

  // Redirect to onboarding if needed
  if (needsOnboarding && location !== '/admin/onboarding') {
    return <Redirect to="/admin/onboarding" />;
  }

  const allowed = allow ?? (requiredRole ? ROLE_SETS[requiredRole] : undefined);
  if (allowed && !allowed.includes(user?.role ?? 'customer')) {
    // Crew see nothing but their own schedule and estimators their own quotes: any other area sends them home.
    return <Redirect to={user?.role === 'crew' || user?.role === 'estimator' ? homePathForRole(user.role) : '/unauthorized'} />;
  }

  return <>{children}</>;
}
